import {
  type AgendaCaptura,
  type DiaSemana,
  horarioDosMinutos,
  horariosDoDia,
} from '@fiscoai/contracts';

/** Ordem da semana de trabalho: "de segunda a sexta" lê melhor que começar no domingo. */
const ORDEM: readonly DiaSemana[] = ['SEG', 'TER', 'QUA', 'QUI', 'SEX', 'SAB', 'DOM'];

export const DIA_POR_EXTENSO: Readonly<Record<DiaSemana, string>> = {
  SEG: 'Segunda-feira',
  TER: 'Terça-feira',
  QUA: 'Quarta-feira',
  QUI: 'Quinta-feira',
  SEX: 'Sexta-feira',
  SAB: 'Sábado',
  DOM: 'Domingo',
};
export const DIA_CURTO: Readonly<Record<DiaSemana, string>> = {
  SEG: 'Seg',
  TER: 'Ter',
  QUA: 'Qua',
  QUI: 'Qui',
  SEX: 'Sex',
  SAB: 'Sáb',
  DOM: 'Dom',
};
const SINGULAR: Readonly<Record<DiaSemana, string>> = {
  SEG: 'segunda',
  TER: 'terça',
  QUA: 'quarta',
  QUI: 'quinta',
  SEX: 'sexta',
  SAB: 'sábado',
  DOM: 'domingo',
};
/** Plural com o artigo ("às segundas", "aos sábados"). */
const PLURAL: Readonly<Record<DiaSemana, string>> = {
  SEG: 'às segundas',
  TER: 'às terças',
  QUA: 'às quartas',
  QUI: 'às quintas',
  SEX: 'às sextas',
  SAB: 'aos sábados',
  DOM: 'aos domingos',
};

/** "a", "a e b", "a, b e c". */
export function juntar(itens: readonly string[]): string {
  if (itens.length <= 1) return itens[0] ?? '';
  return `${itens.slice(0, -1).join(', ')} e ${itens.at(-1) ?? ''}`;
}

export function descreverDias(dias: readonly DiaSemana[]): string {
  const indices = [...new Set(dias)].map((d) => ORDEM.indexOf(d)).sort((a, b) => a - b);
  if (indices.length === ORDEM.length) return 'todos os dias';
  const seguidos = indices.every((v, i) => i === 0 || v === (indices[i - 1] ?? 0) + 1);
  const primeiro = ORDEM[indices[0] ?? 0] ?? 'SEG';
  const ultimo = ORDEM[indices.at(-1) ?? 0] ?? 'SEG';
  if (seguidos && indices.length >= 3) return `de ${SINGULAR[primeiro]} a ${SINGULAR[ultimo]}`;
  const nomes = indices.map((i) => PLURAL[ORDEM[i] ?? 'SEG']);
  // "às segundas e quartas": o artigo só se repete quando muda (aos sábados).
  const enxutos = nomes.map((n, i) =>
    i > 0 && n.startsWith('às') && (nomes[i - 1] ?? '').startsWith('às') ? n.slice(3) : n,
  );
  return juntar(enxutos);
}

export function descreverIntervalo(minutos: number): string {
  return minutos % 60 === 0 ? `${String(minutos / 60)} h` : `${String(minutos)} min`;
}

/** Leitura da agenda em uma frase: é a legenda da régua e o texto do leitor de tela. */
export function descreverAgenda(agenda: AgendaCaptura): string {
  if (agenda.tipo === 'HORARIOS') {
    const horas = juntar(horariosDoDia(agenda).map(horarioDosMinutos));
    const quantas = agenda.horarios.length === 1 ? 'Consulta' : 'Consultas';
    return `${quantas} às ${horas}, ${descreverDias(agenda.dias)}.`;
  }
  const porDia = horariosDoDia(agenda).length;
  const intervalo = `A cada ${descreverIntervalo(agenda.intervaloMinutos)}`;
  if (agenda.tipo === 'JANELA')
    return `${intervalo}, das ${agenda.inicio} às ${agenda.fim}, ${descreverDias(agenda.dias)} (${String(porDia)} consultas por dia).`;
  return `${intervalo}, o dia todo, todos os dias (${String(porDia)} consultas por dia).`;
}
